/**
 * look.js — Carousel Composer: a photo's rotation and adjustments.
 *
 * A "look" is { rot, corner, adj } and lives on the photo, so a frame used in
 * three slides looks the same in all three. A cell can opt out with look:'none'
 * and show the original.
 *
 * ROTATION IS BAKED. Nothing downstream learns about angles: the rotated pixels
 * are drawn into a canvas once (preview size, and again at export size), and the
 * photo simply reports the baked dimensions. Crops, panorama seams and edge
 * snapping keep working on an axis-aligned image, exactly as they were tested.
 *   corner 'zoom' — crop to the largest centred rectangle of the photo's own
 *                   aspect that fits inside the turned frame: no empty corners.
 *   corner 'fill' — keep the whole turned frame; the corners fall through to the
 *                   slide's fill colour.
 *
 * ADJUSTMENTS ARE BAKED, by lib/carousel/pipeline.js — the one pixel pipeline the
 * cell previews, the editor stage and the export all go through, so what you see
 * is what saves. Each is -100..100, 0 = untouched. filterString() below survives
 * only as a cheap CSS approximation for thumbnails; ctx.filter is gone.
 */

/**
 * ADJUSTMENTS — Light and Colour, each -100..100 with 0 meaning untouched.
 *
 * The four original controls (brightness, contrast, saturation, warmth) live on
 * in saved carousels and drafts, so normalizeAdj() migrates them: b -> ex,
 * c -> ct, s -> sa, w -> tp. Nothing needs rewriting on disk.
 *
 * Highlights, shadows, whites and blacks cannot be expressed as CSS filters, so
 * cells, the editor and the export all render through lib/carousel/pipeline.js.
 * filterString() below remains an APPROXIMATION for thumbnails only.
 */
export const ADJ_GROUPS = [
  { key: 'light', label: 'Light', keys: ['ex', 'ct', 'hi', 'sh', 'wh', 'bl'] },
  { key: 'colour', label: 'Colour', keys: ['tp', 'tn', 'sa', 'vb'] },
];

export const ADJ_KEYS = ADJ_GROUPS.flatMap((g) => g.keys);

export const ADJ_LABELS = {
  ex: 'Exposure', ct: 'Contrast', hi: 'Highlights', sh: 'Shadows', wh: 'Whites', bl: 'Blacks',
  tp: 'Temperature', tn: 'Tint', sa: 'Saturation', vb: 'Vibrance',
};

/** What the slider ends read, so "warmer / cooler" beats "-100 / 100". */
export const ADJ_ENDS = {
  ex: ['darker', 'brighter'], ct: ['flatter', 'punchier'], hi: ['recovered', 'blown'],
  sh: ['crushed', 'lifted'], wh: ['duller', 'brighter'], bl: ['lifted', 'deeper'],
  tp: ['cooler', 'warmer'], tn: ['greener', 'magenta'], sa: ['muted', 'saturated'],
  vb: ['muted', 'vivid'],
};

const LEGACY = { b: 'ex', c: 'ct', s: 'sa', w: 'tp' };

export const NEUTRAL_ADJ = Object.freeze(Object.fromEntries(ADJ_KEYS.map((k) => [k, 0])));
export const DEFAULT_LOOK = { rot: 0, corner: 'zoom', adj: NEUTRAL_ADJ };

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const round3 = (v) => Math.round(v * 1000) / 1000;

export function normalizeAngle(deg) {
  const d = Number(deg) || 0;
  const r = d % 360;
  return round3(r > 180 ? r - 360 : r <= -180 ? r + 360 : r);
}

export function normalizeAdj(adj) {
  const a = adj || {};
  const out = {};
  for (const k of ADJ_KEYS) out[k] = clamp(Math.round(Number(a[k]) || 0), -100, 100);
  // a carousel saved before the Light/Colour set carries the original four
  for (const [from, to] of Object.entries(LEGACY)) {
    if (a[from] != null && a[to] == null) out[to] = clamp(Math.round(Number(a[from]) || 0), -100, 100);
  }
  return out;
}

export function normalizeLook(look) {
  const l = look || {};
  return {
    rot: normalizeAngle(l.rot),
    corner: l.corner === 'fill' ? 'fill' : 'zoom',
    adj: normalizeAdj(l.adj),
  };
}

export function isNeutralAdj(adj) {
  const a = normalizeAdj(adj);
  return ADJ_KEYS.every((k) => !a[k]);
}

export function isPlainLook(look) {
  const l = normalizeLook(look);
  return !l.rot && isNeutralAdj(l.adj);
}

/** The look a cell actually renders with: the photo's, unless it opted out. */
export function cellLook(im, cell) {
  if (cell && cell.look === 'none') return DEFAULT_LOOK;
  return normalizeLook(im && im.look);
}

/**
 * Cache key for the turned, UNGRADED base pixels. Adjustments are deliberately
 * absent: the grade is a pass over those pixels, so one bake feeds every setting
 * of the sliders. The graded result is cached under lookKey() instead.
 */
export function bakeKey(look) {
  const l = normalizeLook(look);
  return l.rot + '|' + l.corner;
}

/** Cache key for anything derived from the whole look. */
export function lookKey(look) {
  const l = normalizeLook(look);
  // every adjustment, in a fixed order: preview blobs are cached under this, so a
  // key that misses a control silently serves the wrong pixels back
  return l.rot + '|' + l.corner + '|' + ADJ_KEYS.map((k) => l.adj[k]).join(',');
}

/* ---------------- rotation geometry ---------------- */

const rad = (deg) => (deg * Math.PI) / 180;

/** The turned frame's bounding box. */
export function rotatedBox(w, h, deg) {
  const t = rad(normalizeAngle(deg));
  const c = Math.abs(Math.cos(t)), s = Math.abs(Math.sin(t));
  return { w: w * c + h * s, h: w * s + h * c };
}

/**
 * The largest centred rectangle of the SAME aspect as the photo that fits inside
 * the turned frame. A rect (W,H) fits when its un-turned bounding box fits the
 * original: W·c + H·s ≤ w and W·s + H·c ≤ h.
 */
export function inscribedBox(w, h, deg) {
  const t = rad(normalizeAngle(deg));
  const c = Math.abs(Math.cos(t)), s = Math.abs(Math.sin(t));
  const a = w / h;
  const H = Math.min(w / (a * c + s), h / (a * s + c));
  return { w: Math.max(1, a * H), h: Math.max(1, H) };
}

/** A quarter turn has no empty corners, so there is nothing to crop away. */
export function isQuarterTurn(deg) {
  return Math.abs(normalizeAngle(deg) % 90) < 1e-6;
}

/** Baked size for a source of w x h under this look. */
export function bakedBox(w, h, look) {
  const l = normalizeLook(look);
  if (!l.rot) return { w, h };
  if (l.corner === 'fill' || isQuarterTurn(l.rot)) return rotatedBox(w, h, l.rot);
  return inscribedBox(w, h, l.rot);
}

/** A photo record as the crop maths should see it: baked dimensions. */
export function viewPhoto(im, look) {
  const l = normalizeLook(look === undefined ? im && im.look : look);
  const sw = im.srcW || im.w, sh = im.srcH || im.h;
  if (!l.rot) return (sw === im.w && sh === im.h) ? im : { ...im, w: sw, h: sh };
  const b = bakedBox(sw, sh, l);
  const w = Math.max(1, Math.round(b.w)), h = Math.max(1, Math.round(b.h));
  return (w === im.w && h === im.h) ? im : { ...im, w, h };
}

/**
 * Draws a source turned by look.rot into `ctx`, sized outW x outH (which must be
 * bakedBox * scale). Centred, so 'zoom' crops the corners off by construction.
 */
export function drawBaked(ctx, source, srcW, srcH, look, outW, outH) {
  const l = normalizeLook(look);
  const scale = outW / bakedBox(srcW, srcH, l).w;
  ctx.save();
  ctx.imageSmoothingQuality = 'high';
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate(rad(l.rot));
  ctx.drawImage(source, (-srcW * scale) / 2, (-srcH * scale) / 2, srcW * scale, srcH * scale);
  ctx.restore();
}

/* ---------------- adjustments ---------------- */

/**
 * A CSS approximation, for THUMBNAILS ONLY (tiles, filmstrip, tray, strip).
 * Cells, the editor and the export go through pipeline.js, which does the whole
 * set properly; this exists so a 120px thumbnail can carry the look cheaply.
 * Highlights, shadows, whites and blacks have no filter primitive, so they are
 * folded in as a gentle contrast/brightness nudge rather than ignored.
 */
export function filterString(adj) {
  const a = normalizeAdj(adj);
  const parts = [];
  const bright = a.ex + a.wh * 0.25 + a.sh * 0.2;
  const contrast = a.ct + (a.bl * 0.25 - a.hi * 0.2);
  if (bright) parts.push('brightness(' + round3(1 + bright / 100) + ')');
  if (contrast) parts.push('contrast(' + round3(1 + contrast / 100) + ')');
  const sat = a.sa + a.vb * 0.6;
  if (sat) parts.push('saturate(' + round3(1 + sat / 100) + ')');
  if (a.tp || a.tn) {
    const m = Math.min(1, (Math.abs(a.tp) + Math.abs(a.tn) * 0.6) / 100);
    const sep = 'sepia(' + round3(m * 0.55) + ')';
    // Cooling is the same sepia read backwards, so the turn has to happen AROUND
    // it and come back: rotating after the sepia turns every hue in the frame,
    // which previewed green foliage as purple and only read as "cooler" on grey.
    if (a.tp < 0) parts.push('hue-rotate(180deg)', sep, 'hue-rotate(-180deg)');
    else parts.push(sep);
    // tint is its own turn, and it belongs at both signs of temperature
    if (a.tn) parts.push('hue-rotate(' + round3(a.tn * 0.25) + 'deg)');
    parts.push('saturate(' + round3(1 + m * 0.35) + ')');
  }
  return parts.join(' ');
}

/** Style object for a preview layer; {} when the look changes nothing. */
export function filterStyle(adj) {
  const f = filterString(adj);
  return f ? { filter: f } : null;
}
